import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTenantMutation, TENANT_KEYS } from "../../hooks/useTenants";
import { useBrandsQuery } from "../../hooks/useBrands";
import type { Tenant } from "../../services/tenantApi";

/**
 * Normalizes NIP for validation (removes spaces, dashes, leading "PL").
 */
const normalizeNip = (nip: string): string =>
  nip.trim().toUpperCase().replace(/^PL/, "").replace(/[\s-]/g, "");

/**
 * Validates NIP — exactly 10 digits after normalization.
 * Empty string is considered valid (NIP is optional).
 */
const isValidNip = (nip: string): boolean => {
  const normalized = normalizeNip(nip);
  if (normalized === "") return true;
  return /^\d{10}$/.test(normalized);
};

interface FormData {
  name: string;
  subdomain: string;
  nip: string;
  brandId: string;
  title: string;
  description: string;
}

/**
 * Builds the form data object from tenant props.
 * Extracted to avoid duplication across useMemo, useEffect, and handleCancel.
 */
function buildFormDataFromTenant(tenant: Tenant): FormData {
  return {
    name: tenant.name,
    subdomain: tenant.subdomain,
    nip: tenant.nip || "",
    brandId: tenant.brandId || tenant.brand?.id || "",
    title: tenant.title || "",
    description: tenant.description || "",
  };
}

/**
 * Props for TenantBasicInfoForm.
 * @param tenant - Current tenant data used to initialize and reset the form.
 * @param onSuccess - Optional callback called after a successful save.
 */
interface TenantBasicInfoFormProps {
  tenant: Tenant;
  onSuccess?: () => void;
}

/**
 * Editable form for tenant basic info: name, subdomain, NIP, brand, title, description.
 * Handles subdomain change warning and confirmation dialog (B7).
 * On success: toast + invalidate tenant query. On error: toast, form retains values (B5).
 */
export const TenantBasicInfoForm: React.FC<TenantBasicInfoFormProps> = ({
  tenant,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { data: brands } = useBrandsQuery();
  const updateMutation = useUpdateTenantMutation();

  const initialFormData = useMemo<FormData>(
    () => buildFormDataFromTenant(tenant),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenant.id]
  );

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [nipError, setNipError] = useState<string | null>(null);

  // Sync formData when tenant prop identity changes (e.g., after refetch)
  React.useEffect(() => {
    setFormData(buildFormDataFromTenant(tenant));
    setNipError(null);
    // Intentionally only resync on tenant identity/version change, not on every prop field;
    // editing should not be overwritten by parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, tenant.updatedAt]);

  const isDirty =
    formData.name !== tenant.name ||
    formData.subdomain !== tenant.subdomain ||
    formData.nip !== (tenant.nip || "") ||
    formData.brandId !== (tenant.brandId || tenant.brand?.id || "") ||
    formData.title !== (tenant.title || "") ||
    formData.description !== (tenant.description || "");

  const subdomainChanged = formData.subdomain !== tenant.subdomain;

  const handleCancel = (): void => {
    setFormData(buildFormDataFromTenant(tenant));
    setNipError(null);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    // Validate NIP
    if (!isValidNip(formData.nip)) {
      setNipError(
        "Nieprawidłowy format NIP. Akceptujemy: 1234567890, 123-456-78-90, PL1234567890"
      );
      return;
    }
    setNipError(null);

    // Confirm subdomain change (B7)
    if (subdomainChanged) {
      const confirmed = window.confirm(
        `Zmiana subdomeny z "${tenant.subdomain}" na "${formData.subdomain}" spowoduje niedostępność starego adresu URL.\n\nCzy na pewno chcesz kontynuować?`
      );
      if (!confirmed) return;
    }

    updateMutation.mutate(
      {
        tenantId: tenant.id,
        tenantData: {
          name: formData.name,
          subdomain: formData.subdomain,
          nip: formData.nip || undefined,
          brandId: formData.brandId || undefined,
          title: formData.title || undefined,
          description: formData.description || undefined,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenant.id) });
          onSuccess?.();
        },
        // On error: toast is shown by useUpdateTenantMutation, form NOT cleared (B5)
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label
          htmlFor={`tenant-name-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Nazwa
        </label>
        <input
          id={`tenant-name-${tenant.id}`}
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Subdomain */}
      <div>
        <label
          htmlFor={`tenant-subdomain-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Subdomena
        </label>
        <input
          id={`tenant-subdomain-${tenant.id}`}
          type="text"
          required
          value={formData.subdomain}
          onChange={(e) =>
            setFormData({ ...formData, subdomain: e.target.value })
          }
          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
          pattern="^[a-z0-9\-]+$"
          title="Tylko małe litery, cyfry i myślniki"
        />
        {/* Inline warning for subdomain change (B7) */}
        {subdomainChanged && (
          <p className="text-xs text-amber-600 mt-1">
            Zmiana subdomeny spowoduje niedostępność starego adresu URL.
          </p>
        )}
      </div>

      {/* NIP */}
      <div>
        <label
          htmlFor={`tenant-nip-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          NIP
        </label>
        <input
          id={`tenant-nip-${tenant.id}`}
          type="text"
          value={formData.nip}
          onChange={(e) => {
            setFormData({ ...formData, nip: e.target.value });
            if (nipError) setNipError(null);
          }}
          aria-invalid={!!nipError}
          aria-describedby={nipError ? `nip-error-${tenant.id}` : `nip-hint-${tenant.id}`}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            nipError ? "border-red-400" : "border-neutral-300"
          }`}
          placeholder="1234567890 lub PL1234567890"
        />
        {nipError ? (
          <p id={`nip-error-${tenant.id}`} role="alert" className="text-xs text-red-600 mt-1">{nipError}</p>
        ) : (
          <p id={`nip-hint-${tenant.id}`} className="text-xs text-neutral-500 mt-1">
            Akceptujemy formaty: 1234567890, 123-456-78-90, 123 456 78 90,
            PL1234567890
          </p>
        )}
      </div>

      {/* Brand */}
      <div>
        <label
          htmlFor={`tenant-brand-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Brand
        </label>
        <select
          id={`tenant-brand-${tenant.id}`}
          value={formData.brandId}
          onChange={(e) =>
            setFormData({ ...formData, brandId: e.target.value })
          }
          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">Wybierz brand (opcjonalnie)</option>
          {brands?.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.displayName}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label
          htmlFor={`tenant-title-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Tytuł
        </label>
        <input
          id={`tenant-title-${tenant.id}`}
          type="text"
          value={formData.title}
          onChange={(e) =>
            setFormData({ ...formData, title: e.target.value })
          }
          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Opcjonalny tytuł tenanta"
        />
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor={`tenant-description-${tenant.id}`}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Opis
        </label>
        <textarea
          id={`tenant-description-${tenant.id}`}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          rows={3}
          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Opcjonalny opis tenanta"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Anuluj
        </button>
        <button
          type="submit"
          disabled={!isDirty || updateMutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium transition-colors text-sm"
        >
          {updateMutation.isPending ? "Zapisywanie..." : "Zapisz"}
        </button>
      </div>
    </form>
  );
};

export default TenantBasicInfoForm;
